import { FieldProps } from "formik";
import { Autocomplete } from "formik-mui";
import {
  AutocompleteRenderInputParams,
  debounce,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ApiError, Zahllauf, ZahllaeufePage } from "../../api";
import { useGetZahllauf, useSucheZahllaeufe } from "../../client/hooks";

interface ZahllaufSelectProps extends FieldProps {
  label: string;
  required?: boolean;
  zahllaufBezahlt?: boolean;
}

function ZahllaufSelect({
  label,
  required,
  zahllaufBezahlt,
  field,
  form,
  meta,
}: ZahllaufSelectProps) {
  const searchZahllaeufe = useSucheZahllaeufe();
  const ladeZahllauf = useGetZahllauf();
  const [optionen, setOptionen] = useState<ReadonlyArray<Zahllauf>>([]);
  const [inputValue, setInputValue] = useState(field.value);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    form.setFieldValue(field.name, inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (field.value && typeof field.value === "string") {
      ladeZahllauf(field.value)
        .then((zahllauf) => {
          form.setFieldValue(field.name, zahllauf);
          setInputValue(zahllauf);
        })
        .catch((error) => {
          console.error("Error loading zahllauf:", error);
        });
    }
  }, [field.value]);

  const ladeOptionen = useMemo(
    () =>
      debounce(
        async (
          request: { input: string },
          callback: (error?: ApiError, results?: ZahllaeufePage) => void
        ) =>
          searchZahllaeufe({
            zahllaufBezahlt: zahllaufBezahlt,
            search: request.input,
            page: 0,
            limit: 20,
            sort: "bezeichnung",
          })
            .then((results) => {
              callback(undefined, results);
            })
            .catch((error) => callback(error)),
        400
      ),
    [searchZahllaeufe]
  );

  useEffect(() => {
    ladeOptionen({ input: searchValue }, (error, results) => {
      if (error) console.error("Error:", error);
      setOptionen([...(results ? results.items : [])]);
    });
  }, [searchValue, ladeOptionen]);

  return (
    <Autocomplete
      field={field}
      form={form}
      meta={meta}
      filterSelectedOptions={true}
      filterOptions={(x) => x} // Deaktiviert die Filterlogik, damit komplett nur in dem Backend gesucht werden kann.
      options={optionen}
      getOptionLabel={(option: string | Zahllauf) => {
        if (typeof option === "string") {
          return option;
        } else {
          return option.bezeichnung;
        }
      }}
      onChange={(event, value) => {
        setInputValue(value);
      }}
      onInputChange={(event, value) => {
        setSearchValue(value);
      }}
      renderOption={(props, option: Zahllauf) => {
        return (
          <ListItem {...props} key={option.id}>
            <ListItemText primary={option.bezeichnung} />
          </ListItem>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField {...params} label={label + (required ? " *" : "")} />
      )}
    />
  );
}

export default ZahllaufSelect;
